import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Navigation } from "@/components/layout/Navigation";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { JourneyMap } from "@/components/JourneyMap";
import ShopFooter from "@/components/ShopFooter";
import { Badge, BADGES } from "@/components/Badges";
import axios from "axios";
import { toast } from "sonner";
import { 
  ArrowRight, 
  BookOpen, 
  Flame, 
  Lock, 
  CheckCircle2,
  Sparkles,
  Compass,
  PenLine,
  Archive,
  Calendar,
  Target,
  Clock,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Sun,
  Quote,
  Send,
  Library,
  Feather,
  RefreshCw,
  Gift,
  ExternalLink,
  Award,
  CalendarDays,
  BarChart3
} from "lucide-react";

const COURSE_ICONS = {
  "course_wakeup": Sparkles,
  "course_burnmap": Flame,
  "course_whole": Compass
};

const COURSE_ORDER = ["course_wakeup", "course_burnmap", "course_whole"];

// Weekly reflection questions
const WEEKLY_REFLECTION_PROMPTS = [
  "What was your biggest insight this week?",
  "What patterns did you notice in yourself?",
  "What are you grateful for from this week?",
  "What would you like to carry forward into next week?",
  "What are you ready to release or let go of?"
];

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signalResponse, setSignalResponse] = useState("");
  const [savingSignal, setSavingSignal] = useState(false);
  const [journalHistory, setJournalHistory] = useState([]);
  const [expandedEntry, setExpandedEntry] = useState(null);
  const [weeklyResponse, setWeeklyResponse] = useState("");
  const [savingWeekly, setSavingWeekly] = useState(false);
  const [showWeeklyReflection, setShowWeeklyReflection] = useState(false);
  const [trialStatus, setTrialStatus] = useState(null);
  const [earnedBadges, setEarnedBadges] = useState([]);
  const [aiWeeklyReflection, setAiWeeklyReflection] = useState(null);
  const [aiReflectionExpanded, setAiReflectionExpanded] = useState(false);

  useEffect(() => {
    if (token) {
      fetchDashboard();
      fetchJournalHistory();
      checkWeeklyReflection();
      fetchTrialStatus();
      fetchBadges();
      fetchAIWeeklyReflection();
    }
  }, [token]);

  const fetchBadges = async () => {
    try {
      const response = await axios.get(`${API}/gamification/stats`, {
        withCredentials: true
      });
      setEarnedBadges(response.data?.badges || []);
    } catch (error) {
      // Badges are supplementary - graceful degradation
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch badges:', error);
      }
    }
  };

  const fetchAIWeeklyReflection = async () => {
    try {
      const response = await axios.get(`${API}/pathfinder/weekly-reflection`, {
        withCredentials: true
      });
      if (response.data.has_access && response.data.reflection) {
        setAiWeeklyReflection({
          content: response.data.reflection,
          weekId: response.data.week_id,
          generatedAt: response.data.generated_at,
          checkinCount: response.data.checkin_count,
          journalCount: response.data.journal_count,
          isNew: response.data.is_new
        });
        // Auto-expand if it's a new reflection
        if (response.data.is_new) {
          setAiReflectionExpanded(true);
        }
      }
    } catch (error) {
      // AI reflection is optional - fail silently
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch AI weekly reflection:', error);
      }
    }
  };

  const fetchTrialStatus = async () => {
    try {
      const response = await axios.get(`${API}/trial/status`, {
        withCredentials: true
      });
      setTrialStatus(response.data);
    } catch (error) {
      // Trial status is optional - fail silently as this doesn't affect core functionality
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch trial status:', error);
      }
    }
  };

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/dashboard`, {
        withCredentials: true
      });
      setDashboard(response.data);
      if (response.data.todays_signal?.user_response) {
        setSignalResponse(response.data.todays_signal.user_response);
      }
    } catch (error) {
      console.error("Dashboard error:", error);
      // Only show error toast for genuine failures, not initial auth issues
      if (error.response?.status !== 401) {
        toast.error("Failed to load dashboard");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchJournalHistory = async () => {
    try {
      const response = await axios.get(`${API}/journal?limit=5`, {
        withCredentials: true
      });
      setJournalHistory(response.data || []);
    } catch (error) {
      console.error("Journal history error:", error);
    }
  };

  const checkWeeklyReflection = () => {
    // Show weekly reflection every 7 days (check day of year % 7 === 0)
    const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
    const lastWeeklyReflection = localStorage.getItem('lastWeeklyReflection');
    const currentWeek = Math.floor(dayOfYear / 7);
    
    if (!lastWeeklyReflection || parseInt(lastWeeklyReflection) < currentWeek) {
      setShowWeeklyReflection(true);
    }
  };

  const handleSignalResponse = async () => {
    if (!signalResponse.trim() || !dashboard?.todays_signal) return;
    
    setSavingSignal(true);
    try {
      await axios.post(`${API}/signals/respond`, {
        signal_id: dashboard.todays_signal.signal_id,
        response_text: signalResponse
      }, { withCredentials: true });
      
      setDashboard(prev => ({
        ...prev,
        todays_signal: {
          ...prev.todays_signal,
          responded: true,
          user_response: signalResponse
        }
      }));
      toast.success("Reflection saved!");
      fetchJournalHistory(); // Refresh journal history
    } catch (error) {
      console.error("Signal response error:", error);
      toast.error("Failed to save reflection");
    } finally {
      setSavingSignal(false);
    }
  };

  const handleWeeklyReflection = async () => {
    if (!weeklyResponse.trim()) return;
    
    setSavingWeekly(true);
    try {
      await axios.post(`${API}/journal`, {
        title: "Weekly Reflection",
        body: weeklyResponse,
        mood: null,
        tags: ["weekly-reflection"],
        is_draft: false
      }, { withCredentials: true });
      
      // Mark weekly reflection as done
      const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
      const currentWeek = Math.floor(dayOfYear / 7);
      localStorage.setItem('lastWeeklyReflection', currentWeek.toString());
      
      setShowWeeklyReflection(false);
      setWeeklyResponse("");
      toast.success("Weekly reflection saved!");
      fetchJournalHistory();
    } catch (error) {
      console.error("Weekly reflection error:", error);
      toast.error("Failed to save reflection");
    } finally {
      setSavingWeekly(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
    });
  };

  const getProgressPercentage = () => {
    if (!dashboard?.courses) return 0;
    const totalLessons = dashboard.courses.reduce((sum, c) => sum + (c.total_lessons || 0), 0);
    const completedLessons = dashboard.courses.reduce((sum, c) => sum + (c.completed_lessons || 0), 0);
    return totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;
  };

  const getTotalCompletedLessons = () => {
    if (!dashboard?.courses) return 0;
    return dashboard.courses.reduce((sum, c) => sum + (c.completed_lessons || 0), 0);
  };

  const getTotalLessons = () => {
    if (!dashboard?.courses) return 0;
    return dashboard.courses.reduce((sum, c) => sum + (c.total_lessons || 0), 0);
  };

  // Don't block rendering - show content with placeholder data while loading
  const isDataLoaded = !loading && dashboard;

  const sortedCourses = dashboard?.courses 
    ? [...dashboard.courses].sort((a, b) => 
        COURSE_ORDER.indexOf(a.course_id) - COURSE_ORDER.indexOf(b.course_id)
      )
    : [];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <main className="pt-20 pb-12 px-4 sm:px-6 max-w-5xl mx-auto">
        {/* Welcome Header */}
        <header className="mb-8 animate-fade-in">
          <h1 className="text-2xl sm:text-3xl font-heading text-white mb-1">
            {loading ? (
              <span className="animate-pulse">Welcome back...</span>
            ) : (
              `Welcome back, ${dashboard?.user?.name || user?.name || 'Friend'}`
            )}
          </h1>
          <p className="text-white/50">Your transformation journey continues.</p>
        </header>

        {/* Trial Banner */}
        {trialStatus?.is_active && trialStatus.days_remaining > 0 && (
          <div className="mb-6 bg-gradient-to-r from-[#a69373]/20 to-[#a69373]/10 border border-[#a69373]/30 rounded-xl p-4 animate-fade-in">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#a69373]/20 flex items-center justify-center">
                  <Gift className="w-4 h-4 text-[#a69373]" />
                </div>
                <div>
                  <p className="text-white font-medium text-sm">
                    Trial active — {trialStatus.days_remaining} day{trialStatus.days_remaining !== 1 ? 's' : ''} remaining
                  </p>
                  <p className="text-white/50 text-xs">Upgrade to keep access after your trial ends</p>
                </div>
              </div>
              <a
                href="https://www.truenorth-zone.com/pages/compass-membership"
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-[#a69373] hover:bg-[#8b7a5e] text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
              >
                Upgrade Now
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        )}

        {/* === AI WEEKLY REFLECTION CARD === */}
        {aiWeeklyReflection && (
          <section 
            className="mb-6 animate-fade-in" 
            style={{ animationDelay: "25ms" }} 
            data-testid="ai-weekly-reflection-card"
          >
            <Card 
              className="overflow-hidden border transition-all duration-300"
              style={{ 
                backgroundColor: 'rgba(166, 147, 115, 0.08)', 
                borderColor: aiWeeklyReflection.isNew ? 'rgba(166, 147, 115, 0.5)' : 'rgba(166, 147, 115, 0.2)'
              }}
            >
              {/* Collapsible Header */}
              <button
                onClick={() => setAiReflectionExpanded(!aiReflectionExpanded)}
                className="w-full p-4 sm:p-5 flex items-center justify-between hover:bg-[#a69373]/5 transition-colors text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(166, 147, 115, 0.15)' }}>
                    <CalendarDays className="w-6 h-6" style={{ color: '#a69373' }} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-0.5">
                      <h3 className="text-lg font-heading text-white">Your Weekly Reflection</h3>
                      {aiWeeklyReflection.isNew && (
                        <span 
                          className="px-2 py-0.5 text-xs font-semibold rounded-full"
                          style={{ backgroundColor: '#a69373', color: '#0b2821' }}
                        >
                          NEW
                        </span>
                      )}
                    </div>
                    <p className="text-white/50 text-sm">
                      Based on {aiWeeklyReflection.checkinCount} check-in{aiWeeklyReflection.checkinCount !== 1 ? 's' : ''} this week
                      {aiWeeklyReflection.journalCount > 0 && ` & ${aiWeeklyReflection.journalCount} journal entr${aiWeeklyReflection.journalCount !== 1 ? 'ies' : 'y'}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {!aiReflectionExpanded && (
                    <span className="text-white/40 text-sm hidden sm:block">Click to read</span>
                  )}
                  {aiReflectionExpanded ? (
                    <ChevronUp className="w-5 h-5 text-white/40" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-white/40" />
                  )}
                </div>
              </button>
              
              {/* Expandable Content */}
              {aiReflectionExpanded && (
                <CardContent className="px-4 sm:px-5 pb-5 pt-0">
                  <div 
                    className="rounded-xl p-4 sm:p-5"
                    style={{ backgroundColor: 'rgba(11, 40, 33, 0.5)' }}
                  >
                    <p className="whitespace-pre-wrap text-white/90 leading-relaxed">
                      {aiWeeklyReflection.content}
                    </p>
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <p className="text-white/40 text-xs">
                      Generated {new Date(aiWeeklyReflection.generatedAt).toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </p>
                    <Link
                      to="/pathfinder"
                      className="text-xs flex items-center gap-1 hover:underline"
                      style={{ color: '#a69373' }}
                    >
                      Open in PathFinder
                      <ChevronRight className="w-3 h-3" />
                    </Link>
                  </div>
                </CardContent>
              )}
            </Card>
          </section>
        )}

        {/* === SECTION 1: TODAY'S SIGNAL (Full Width, Prominent) === */}
        {dashboard?.todays_signal && (
          <section className="mb-8 animate-fade-in" style={{ animationDelay: "50ms" }} data-testid="todays-signal-section">
            <Card className="glass-card overflow-hidden border-0 shadow-xl">
              {/* Subtle gradient header */}
              <div className="bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 p-6 sm:p-8 border-b border-white/5">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-primary/15 flex items-center justify-center">
                      <Sun className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-xl sm:text-2xl font-heading text-white mb-1">Today's Signal</h2>
                      <p className="text-white/40 text-sm">Day {dashboard.todays_signal.day_number} • {dashboard.todays_signal.quarter?.replace(/_/g, ' ')}</p>
                    </div>
                  </div>
                  {dashboard.todays_signal.responded && (
                    <span className="text-xs text-primary bg-primary/15 px-3 py-1.5 rounded-full flex items-center gap-1.5">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      Completed
                    </span>
                  )}
                </div>
              </div>
              
              <CardContent className="p-6 sm:p-8">
                {/* Signal Title */}
                <h3 className="text-xl sm:text-2xl text-white font-heading mb-4">{dashboard.todays_signal.title}</h3>
                
                {/* Prompt */}
                <p className="text-white/70 text-lg leading-relaxed mb-6">{dashboard.todays_signal.prompt}</p>
                
                {/* Quote */}
                {dashboard.todays_signal.quote && (
                  <div className="bg-white/3 rounded-xl p-5 mb-6 border-l-2 border-primary/30">
                    <div className="flex gap-3">
                      <Quote className="w-5 h-5 text-primary/50 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-white/50 italic leading-relaxed">"{dashboard.todays_signal.quote}"</p>
                        {dashboard.todays_signal.quote_author && (
                          <p className="text-white/30 text-sm mt-2">— {dashboard.todays_signal.quote_author}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Response Input */}
                <div className="space-y-4">
                  <label className="block text-white/50 text-sm font-medium">
                    {dashboard.todays_signal.responded ? "Your Reflection" : "Take a moment to reflect..."}
                  </label>
                  <Textarea
                    value={signalResponse}
                    onChange={(e) => setSignalResponse(e.target.value)}
                    placeholder="What comes up for you? Write freely..."
                    className="min-h-[140px] bg-white/3 border-white/10 text-white placeholder:text-white/25 text-base leading-relaxed resize-none focus:border-primary/30"
                    disabled={dashboard.todays_signal.responded}
                    data-testid="signal-textarea"
                  />
                  
                  {!dashboard.todays_signal.responded && (
                    <Button 
                      className="btn-primary px-8"
                      onClick={handleSignalResponse}
                      disabled={savingSignal || !signalResponse.trim()}
                      data-testid="save-signal-btn"
                    >
                      {savingSignal ? "Saving..." : "Save Reflection"}
                      <Send className="ml-2 w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </section>
        )}

        {/* Main Grid Layout */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* === SECTION 2: THE JOURNEY MAP === */}
            <section className="animate-fade-in" style={{ animationDelay: "100ms" }}>
              <h2 className="text-sm font-medium text-white/50 uppercase tracking-wider mb-3">The Journey Map</h2>
              <JourneyMap variant="full" />
            </section>

            {/* === SECTION 3: WEEKLY REFLECTION === */}
            {showWeeklyReflection && (
              <section className="animate-fade-in" style={{ animationDelay: "200ms" }}>
                <h2 className="text-sm font-medium text-white/50 uppercase tracking-wider mb-3">Weekly Reflection</h2>
                <Card className="glass-card border border-violet-500/20">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
                        <RefreshCw className="w-5 h-5 text-violet-400" />
                      </div>
                      <div>
                        <h3 className="text-white font-medium">Time for Your Weekly Check-In</h3>
                        <p className="text-white/40 text-sm">Reflect on your journey this week</p>
                      </div>
                    </div>
                    
                    <div className="bg-white/3 rounded-lg p-4 mb-4">
                      <p className="text-white/60 text-sm mb-2">Reflection prompts:</p>
                      <ul className="space-y-1.5">
                        {WEEKLY_REFLECTION_PROMPTS.map((prompt) => (
                          <li key={prompt} className="text-white/50 text-sm flex items-start gap-2">
                            <span className="text-violet-400">•</span>
                            {prompt}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <Textarea
                      value={weeklyResponse}
                      onChange={(e) => setWeeklyResponse(e.target.value)}
                      placeholder="Take time to reflect on your week..."
                      className="min-h-[100px] bg-white/3 border-white/10 text-white placeholder:text-white/25 mb-3"
                      data-testid="weekly-textarea"
                    />
                    
                    <div className="flex gap-2">
                      <Button 
                        variant="outline"
                        className="border-white/10 text-white/60 hover:bg-white/5"
                        onClick={() => setShowWeeklyReflection(false)}
                      >
                        Skip for now
                      </Button>
                      <Button 
                        className="btn-primary flex-1"
                        onClick={handleWeeklyReflection}
                        disabled={savingWeekly || !weeklyResponse.trim()}
                      >
                        {savingWeekly ? "Saving..." : "Save Weekly Reflection"}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </section>
            )}

            {/* === SECTION 5: YOUR PROGRAMS === */}
            <section className="animate-fade-in" style={{ animationDelay: "250ms" }}>
              <h2 className="text-sm font-medium text-white/50 uppercase tracking-wider mb-3">Your Programs</h2>
              <div className="space-y-3">
                {sortedCourses.map((course, index) => {
                  const Icon = COURSE_ICONS[course.course_id] || BookOpen;
                  const isLocked = !course.unlocked;
                  
                  return (
                    <Card 
                      key={course.course_id}
                      className={`glass-card transition-all ${isLocked ? 'opacity-60' : 'hover:ring-1 hover:ring-primary/20 cursor-pointer'}`}
                      onClick={() => !isLocked && navigate('/path')}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${isLocked ? 'bg-white/5' : 'bg-primary/20'}`}>
                            {isLocked ? (
                              <Lock className="w-5 h-5 text-white/30" />
                            ) : (
                              <Icon className="w-5 h-5 text-primary" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-white font-medium">{course.title}</h3>
                            <p className="text-white/40 text-sm">
                              {isLocked ? 'Locked' : `${course.completion_percentage}% complete`}
                            </p>
                          </div>
                          {!isLocked && (
                            <div className="w-16">
                              <Progress value={course.completion_percentage} className="h-1.5" />
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </section>

            {/* === SECTION 6: JOURNAL HISTORY === */}
            <section className="animate-fade-in" style={{ animationDelay: "300ms" }}>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-medium text-white/50 uppercase tracking-wider">Journal History</h2>
                <Link to="/journal" className="text-primary text-sm hover:underline">View All</Link>
              </div>
              
              {journalHistory.length > 0 ? (
                <div className="space-y-2">
                  {journalHistory.map((entry) => (
                    <Card 
                      key={entry.entry_id} 
                      className="glass-card clickable-card cursor-pointer"
                      onClick={() => setExpandedEntry(expandedEntry === entry.entry_id ? null : entry.entry_id)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center shrink-0">
                            <Feather className="w-4 h-4 text-white/40" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-white/40 text-xs">{formatDate(entry.created_at)}</span>
                              {entry.tags?.includes('daily-signal') && (
                                <span className="text-[10px] text-primary bg-primary/10 px-1.5 py-0.5 rounded">Signal</span>
                              )}
                              {entry.tags?.includes('weekly-reflection') && (
                                <span className="text-[10px] text-violet-400 bg-violet-400/10 px-1.5 py-0.5 rounded">Weekly</span>
                              )}
                            </div>
                            <h4 className="text-white font-medium text-sm mb-1 line-clamp-1">{entry.title}</h4>
                            <p className={`text-white/50 text-sm ${expandedEntry === entry.entry_id ? '' : 'line-clamp-2'}`}>
                              {entry.body?.replace(/\*\*/g, '').substring(0, expandedEntry === entry.entry_id ? undefined : 120)}
                              {entry.body?.length > 120 && expandedEntry !== entry.entry_id && '...'}
                            </p>
                          </div>
                          <ChevronDown className={`w-4 h-4 text-white/30 transition-transform ${expandedEntry === entry.entry_id ? 'rotate-180' : ''}`} />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card className="glass-card">
                  <CardContent className="p-6 text-center">
                    <Feather className="w-8 h-8 text-white/20 mx-auto mb-2" />
                    <p className="text-white/40 text-sm">No journal entries yet</p>
                    <p className="text-white/30 text-xs mt-1">Complete Today's Signal to start your journal</p>
                  </CardContent>
                </Card>
              )}
            </section>
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Stats Grid */}
            <section className="animate-fade-in" style={{ animationDelay: "350ms" }}>
              <div className="grid grid-cols-2 gap-3">
                <Card className="glass-card stat-card">
                  <CardContent className="p-4 text-center">
                    <Target className="stat-icon w-5 h-5 text-primary mx-auto mb-2" />
                    <p className="text-2xl font-heading text-white">{getProgressPercentage()}%</p>
                    <p className="text-xs text-white/40">Progress</p>
                  </CardContent>
                </Card>
                <Card className="glass-card stat-card">
                  <CardContent className="p-4 text-center">
                    <BookOpen className="stat-icon w-5 h-5 text-primary mx-auto mb-2" />
                    <p className="text-2xl font-heading text-white">{getTotalCompletedLessons()}</p>
                    <p className="text-xs text-white/40">Lessons</p>
                  </CardContent>
                </Card>
                <Card className="glass-card stat-card">
                  <CardContent className="p-4 text-center">
                    <Calendar className="stat-icon w-5 h-5 text-primary mx-auto mb-2" />
                    <p className="text-2xl font-heading text-white">{dashboard?.streak || 0}</p>
                    <p className="text-xs text-white/40">Day Streak</p>
                  </CardContent>
                </Card>
                <Card className="glass-card stat-card">
                  <CardContent className="p-4 text-center">
                    <PenLine className="stat-icon w-5 h-5 text-primary mx-auto mb-2" />
                    <p className="text-2xl font-heading text-white">{dashboard?.journal_count || 0}</p>
                    <p className="text-xs text-white/40">Reflections</p>
                  </CardContent>
                </Card>
              </div>
            </section>

            {/* === MY PATTERNS LINK === */}
            <section className="animate-fade-in" style={{ animationDelay: "380ms" }}>
              <Link 
                to="/patterns"
                className="block"
                data-testid="patterns-link"
              >
                <Card className="glass-card group hover:ring-1 hover:ring-primary/20 transition-all cursor-pointer">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                          <BarChart3 className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <h3 className="text-white font-medium">My Patterns</h3>
                          <p className="text-white/50 text-sm">See your trends & insights</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-white/30 group-hover:text-primary transition-colors" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </section>

            {/* Active Quest */}
            {dashboard?.active_quest && (
              <section className="animate-fade-in" style={{ animationDelay: "400ms" }}>
                <h2 className="text-sm font-medium text-white/50 uppercase tracking-wider mb-3">Active Quest</h2>
                <Card className="glass-card">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-9 h-9 rounded-lg bg-amber-500/20 flex items-center justify-center">
                        <Target className="w-4 h-4 text-amber-400" />
                      </div>
                      <h3 className="text-white font-medium text-sm flex-1">{dashboard.active_quest.title}</h3>
                    </div>
                    <p className="text-white/50 text-sm mb-3 line-clamp-2">{dashboard.active_quest.task}</p>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="w-full border-white/10 text-white/70 hover:bg-white/5"
                      onClick={() => dashboard.active_quest.lesson_id && navigate(`/lesson/${dashboard.active_quest.lesson_id}`)}
                    >
                      View Quest
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </CardContent>
                </Card>
              </section>
            )}

            {/* === SECTION 7: VAULT === */}
            <section className="animate-fade-in" style={{ animationDelay: "450ms" }}>
              <h2 className="text-sm font-medium text-white/50 uppercase tracking-wider mb-3">Vault</h2>
              <Card className="glass-card group hover:ring-1 hover:ring-primary/20 transition-all cursor-pointer" onClick={() => navigate('/vault')}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                      <Library className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-white font-medium">Resource Library</h3>
                      <p className="text-white/40 text-sm">Workbooks, guides & exercises</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-white/30 group-hover:text-primary transition-colors" />
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* Quick Links */}
            <section className="animate-fade-in" style={{ animationDelay: "500ms" }}>
              <div className="space-y-2">
                <Link to="/journal" className="block">
                  <Card className="glass-card group hover:ring-1 hover:ring-primary/20 transition-all">
                    <CardContent className="p-3 flex items-center gap-3">
                      <PenLine className="w-4 h-4 text-white/40" />
                      <span className="text-white/60 text-sm flex-1">Open Journal</span>
                      <ChevronRight className="w-4 h-4 text-white/20 group-hover:text-primary" />
                    </CardContent>
                  </Card>
                </Link>
                <Link to="/discover?tab=tools&scroll=quests" className="block">
                  <Card className="glass-card group hover:ring-1 hover:ring-primary/20 transition-all">
                    <CardContent className="p-3 flex items-center gap-3">
                      <Compass className="w-4 h-4 text-white/40" />
                      <span className="text-white/60 text-sm flex-1">View Full Path</span>
                      <ChevronRight className="w-4 h-4 text-white/20 group-hover:text-primary" />
                    </CardContent>
                  </Card>
                </Link>
              </div>
              
              {/* Badges Section */}
              <div className="mt-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-white/70 text-sm font-medium flex items-center gap-2">
                    <Award className="w-4 h-4" />
                    Badges to Earn
                  </h3>
                  <Link to="/community?tab=achievements" className="text-primary text-xs hover:underline">
                    View all
                  </Link>
                </div>
                <div className="grid grid-cols-5 gap-2">
                  {Object.values(BADGES).slice(0, 10).map((badge) => {
                    const isEarned = earnedBadges.some(e => e.badge_id === badge.id);
                    const recentlyEarned = earnedBadges.find(e => e.badge_id === badge.id);
                    const isRecent = recentlyEarned && 
                      (new Date() - new Date(recentlyEarned.earned_at)) < 7 * 24 * 60 * 60 * 1000; // within 7 days
                    
                    return (
                      <div
                        key={badge.id}
                        className={`relative aspect-square rounded-lg flex items-center justify-center transition-all ${
                          isEarned 
                            ? isRecent 
                              ? "bg-primary/30 ring-2 ring-primary animate-pulse" 
                              : "bg-white/10"
                            : "bg-white/5 opacity-40"
                        }`}
                        title={`${badge.name}${isEarned ? " (Earned)" : ""}`}
                      >
                        <Badge 
                          badge={badge} 
                          earned={isEarned}
                          size="sm"
                        />
                        {isRecent && (
                          <span className="absolute -top-1 -right-1 w-3 h-3 bg-primary rounded-full animate-bounce" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>
          </div>
        </div>
        
        {/* Shop Footer */}
        <ShopFooter />
      </main>
    </div>
  );
}
